/**
 * Univer column header extension that draws the AI (SmartToy) icon to the right of the header
 * text for columns that have a definition (AI-enabled).
 */
import type { IScale } from '@univerjs/core';
import { SheetExtension, SHEET_EXTENSION_TYPE } from '@univerjs/engine-render';
import type { SpreadsheetSkeleton } from '@univerjs/engine-render';
import type { UniverRenderingContext } from '@univerjs/engine-render';

const AI_ICON_SIZE = 14;
const AI_ICON_PADDING_RIGHT = 4;
/** Minimum gap between end of header text and the icon so they never overlap. */
const GAP_AFTER_TEXT = 12;
const HEADER_TEXT_PADDING_LEFT = 6;
const HEADER_FONT = '12px "Helvetica Neue", Helvetica, Arial, sans-serif';

/** Default blue used by enrichment (theme primary). */
const DEFAULT_ICON_COLOR = '#49BAFD';

/** Build SmartToy icon (MUI) as SVG data URL with the given fill color. */
function getSmartToySvgDataUrl(fillColor: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${fillColor.replace(/"/g, '&quot;')}"><path d="M20 9V7c0-1.1-.9-2-2-2h-3c0-1.66-1.34-3-3-3S9 3.34 9 5H6c-1.1 0-2 .9-2 2v2c-1.66 0-3 1.34-3 3s1.34 3 3 3v4c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4c1.66 0 3-1.34 3-3s-1.34-3-3-3zM7.5 11.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S9.83 13 9 13s-1.5-.67-1.5-1.5zM16 17H8v-2h8zm-1-4c-.83 0-1.5-.67-1.5-1.5S14.17 10 15 10s1.5.67 1.5 1.5S15.83 13 15 13z"/></svg>`;
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

const imageCacheByColor: Record<string, HTMLImageElement> = {};
let iconLoadCallback: (() => void) | null = null;

function getIconImage(
  iconColor: string,
  onLoaded?: () => void
): HTMLImageElement {
  const key = iconColor || DEFAULT_ICON_COLOR;
  const cached = imageCacheByColor[key];
  if (cached?.complete && cached.naturalWidth > 0) {
    return cached;
  }
  const img = new Image();
  iconLoadCallback = onLoaded ?? null;
  img.onload = () => {
    iconLoadCallback?.();
    iconLoadCallback = null;
  };
  img.src = getSmartToySvgDataUrl(key);
  imageCacheByColor[key] = img;
  return img;
}

export type GetAiColumnIndices = () => Set<number>;
export type GetColumnDisplayName = (colIndex: number) => string;
export type OnIconLoaded = () => void;

/**
 * Creates a SheetExtension that draws the AI icon in the column header for each AI column.
 * @param getAiColumnIndices - Callback that returns the set of column indices that are AI-enabled (have a definition).
 * @param onIconLoaded - Optional callback when the icon image has loaded (e.g. to refresh the canvas).
 * @param iconColor - Fill color for the icon (e.g. theme primary / enrichment blue). Defaults to #49BAFD.
 * @param getColumnDisplayName - Callback that returns the display label for each column (used to ensure the icon never overlaps the text).
 */
export function createAIColumnHeaderIconExtension(
  getAiColumnIndices: GetAiColumnIndices,
  onIconLoaded?: OnIconLoaded,
  iconColor: string = DEFAULT_ICON_COLOR,
  getColumnDisplayName?: GetColumnDisplayName
): SheetExtension {
  const fillColor = iconColor || DEFAULT_ICON_COLOR;
  getIconImage(fillColor, onIconLoaded ?? undefined);

  class AIColumnHeaderIconExtension extends SheetExtension {
    override uKey = 'AIColumnHeaderIconExtension';
    override type = SHEET_EXTENSION_TYPE.GRID;
    protected override Z_INDEX = 100;

    override draw(
      ctx: UniverRenderingContext,
      parentScale: IScale,
      skeleton: SpreadsheetSkeleton
    ): void {
      const aiColumns = getAiColumnIndices();
      if (aiColumns.size === 0) return;

      const sk = skeleton as unknown as {
        getColumnCount?: () => number;
        columnHeaderHeight?: number;
        columnWidthAccumulation?: number[];
        rowColumnSegment?: { startColumn: number; endColumn: number };
      };
      const columnCount =
        sk.getColumnCount?.() ?? sk.columnWidthAccumulation?.length ?? 0;
      if (columnCount <= 0) return;

      const scaleX = parentScale?.scaleX ?? 1;
      const scaleY = parentScale?.scaleY ?? 1;
      const tx = this.translateX ?? 0;
      const ty = this.translateY ?? 0;

      const columnHeaderHeight = sk.columnHeaderHeight ?? 24;
      const headerH = columnHeaderHeight * scaleY;
      const iconY = ty + (headerH - AI_ICON_SIZE) / 2;

      const columnWidthAccumulation = sk.columnWidthAccumulation;
      if (
        !Array.isArray(columnWidthAccumulation) ||
        columnWidthAccumulation.length === 0
      )
        return;

      const segment = sk.rowColumnSegment;
      const startCol =
        segment && segment.startColumn >= 0 && segment.endColumn >= 0
          ? segment.startColumn
          : 0;
      const endCol =
        segment && segment.startColumn >= 0 && segment.endColumn >= 0
          ? Math.min(segment.endColumn + 1, columnCount)
          : columnCount;

      const img = getIconImage(fillColor);

      ctx.save();
      ctx.font = HEADER_FONT;

      for (let col = startCol; col < endCol; col++) {
        if (!aiColumns.has(col)) continue;

        const startX = col === 0 ? 0 : (columnWidthAccumulation[col - 1] ?? 0);
        const endX = columnWidthAccumulation[col] ?? startX;
        const cellW = (endX - startX) * scaleX;
        if (cellW < AI_ICON_SIZE + AI_ICON_PADDING_RIGHT) continue;

        const cellLeft = tx + startX * scaleX;
        const rightEdge = tx + endX * scaleX;
        const preferredX = rightEdge - AI_ICON_SIZE - AI_ICON_PADDING_RIGHT;
        const maxX = rightEdge - AI_ICON_SIZE - AI_ICON_PADDING_RIGHT;

        let x = preferredX;
        if (getColumnDisplayName) {
          const label = getColumnDisplayName(col) ?? '';
          const textWidth = ctx.measureText(label).width;
          const minX =
            cellLeft + HEADER_TEXT_PADDING_LEFT + textWidth + GAP_AFTER_TEXT;
          x = Math.max(minX, preferredX);
        }
        x = Math.min(x, maxX);
        if (x < cellLeft) continue;

        if (img.complete && img.naturalWidth > 0) {
          ctx.drawImage(img, x, iconY, AI_ICON_SIZE, AI_ICON_SIZE);
        } else {
          ctx.fillStyle = fillColor + '99';
          ctx.fillRect(x, iconY, AI_ICON_SIZE, AI_ICON_SIZE);
        }
      }

      ctx.restore();
    }
  }

  return new AIColumnHeaderIconExtension();
}
